import { h, Component } from 'preact'
import HeaderNavigation from '../mega-menu/index.sync'
import { z } from 'zod'
import { UserPermissionContext } from '../../server-middleware/userContext'
import { ModulesContext } from '../../utils/globals'
import { ParentCategoryInfo, WidgetSetting } from '../mega-menu/Types'
import { getInsidedData } from '@insided/destination/utils/insidedDataStorage'

function getCategoryId() {
  if (typeof window === 'undefined') {
    return null
  }
  return getInsidedData('content').category.id
}

export interface Props {
  isFederatedSalesforceSearch: boolean
  isFederatedFreshdeskSearch: boolean
  isFederatedSkilljarSearch: boolean
  items: Config['items']
  logo: Config['logo']
  groupCount: number
  communityCategories: ParentCategoryInfo[]
  knowledgeBaseCategories: ParentCategoryInfo[]
  communityCustomerTitle: string
  ssoLoginUrl: string
  newTopicURL: string
}

export const widgetName = 'header_navigation' as const

export const config = z.object({
  items: z.array(z.object({
    key: z.string(),
    visibility: z.boolean(),
    name: z.string()
  })),
  logo: z.string()
}).default({
  items: [],
  logo: ''
})

type Config = z.infer<typeof config>

const getDefaultItems = (props: Props, modules: string[]): WidgetSetting[] => {
  let defaultItems: WidgetSetting[] = [{
    key: 'community',
    visibility: true,
    name: 'Community'
  }]

  if (modules.includes('knowledgeBase')) {
    defaultItems = defaultItems.concat({
      key: 'knowledgeBase',
      visibility: true,
      name: 'Knowledge Base'
    })
  }

  if (modules.includes('ideationV2')) {
    defaultItems = defaultItems.concat({
      key: 'ideation',
      visibility: true,
      name: 'Ideas',
      url: '/ideas'
    })
  }

  if (modules.includes('productUpdates')) {
    defaultItems = defaultItems.concat({
      key: 'productUpdates',
      visibility: true,
      name: 'Product Updates',
      url: '/product-updates'
    })
  }

  if (modules.includes('event')) {
    defaultItems = defaultItems.concat({
      key: 'event',
      visibility: true,
      name: 'Events',
      url: '/events'
    })
  }

  if (modules.includes('group') && props.groupCount) {
    defaultItems = defaultItems.concat({
      key: 'group',
      visibility: true,
      name: 'Groups',
      url: '/groups'
    })
  }

  return defaultItems
}

const getItems = (props: Props, modules: string[]) => {
  if (props.items?.length) {
    const groupIdx = props.items.findIndex(item => item.key === 'group')
    if (groupIdx && !(modules.includes('group') && props.groupCount)) {
      const newItems = props.items.concat([])
      newItems.splice(groupIdx, 1)
      return newItems
    }
    return props.items
  }

  return getDefaultItems(props, modules)
}

export default class WidgetHeaderNavigation extends Component<Props> {
  render () {
    const props = this.props

    return (
      <UserPermissionContext.Consumer>
        {(permissions) => {
          return (
            <ModulesContext.Consumer>
              {(modules) => {
                return (
                  <HeaderNavigation
                    items={getItems(props, modules)}
                    groupCount={props.groupCount}
                    communityCategories={props.communityCategories}
                    knowledgeBaseCategories={props.knowledgeBaseCategories}
                    logo={props.logo}
                    ssoLoginUrl={props.ssoLoginUrl}
                    newTopicURL={props.newTopicURL}
                    communityCustomerTitle={props.communityCustomerTitle}
                    searchInfo={{
                      isFederatedSalesforceSearch: props.isFederatedSalesforceSearch,
                      isFederatedSkilljarSearch: props.isFederatedSkilljarSearch,
                      isFederatedFreshdeskSearch: props.isFederatedFreshdeskSearch,
                      category: getCategoryId() || undefined,
                      isParentCategory: false // TODO implement this and isParentCategory when extending this feature to category page
                    }}
                    permissions={{
                      ideation: permissions.permissions.includes('forum.idea.view') || permissions.permissions.includes('forum-idea-all'),
                      productUpdates: permissions.permissions.includes('forum.product.updates.view') || permissions.permissions.includes('forum-product-updates-all')
                    }}
                  />
                )
              }}
            </ModulesContext.Consumer>
          )
        }}
      </UserPermissionContext.Consumer>
    )
  }
}
